import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { UserContext } from 'contexts/UserContext';
import { AppContext } from 'contexts/AppContext';
import useAsyncPrequalifyOptions from 'pages/ChooseHousehold/hooks';
import useReactHookFormProps from 'hooks/useReactHookFormProps';
import useAffordaPortalDetailsQuery from 'hooks/queries/useAffordaPortalDetailsQuery';
import useUpdateAffordaPortalDetailsMutation from 'hooks/mutations/useUpdateAffordaPortalDetailsMutation';
import useFormStateManager from 'hooks/affordaPortalFormState/useFormStateManager';
import SECTION_NAMES from 'constants/sectionNames';
import { FormattedMessage } from 'react-intl';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';

const FIELDS = [
  {
    variant: 'counter',
    label: <FormattedMessage id="afforda.eligibility.counterLabel" />,
    name: 'numberOfPeopleInUnit',
    isAffordaPortalSpecific: true,
    helperText: <FormattedMessage id="afforda.eligibility.counterHelpText" />,
    rules: {
      required: true,
      max: 10,
      min: 1,
    },
    GridProps: {
      xs: 12,
      sm: 6,
    },
  },
  {
    variant: 'number',
    name: 'totalAnnualHouseholdIncome',
    isAffordaPortalSpecific: true,
    label: <FormattedMessage id="afforda.eligibility.currencyLabel" />,
    helperText: <FormattedMessage id="afforda.eligibility.currencyHelpText" />,
    GridProps: {
      xs: 12,
      sm: 6,
    },
    adornment: '$',
    rules: {
      max: 499999.99,
      min: 0,
      step: 0.01,
      required: true,
    },
    InputLabelProps: {
      TooltipProps: {
        title: <FormattedMessage id="afforda.eligibility.currencyTooltip" />,
        children: <AlertInfoIcon fontSize={'small'} />,
      },
    },
  },
];

export const useContinueButtonProps = ({
  ReactHookFormProps,
  baseURL,
  onSubmit,
  history,
}) => {
  const { pathname } = history.location;
  const isEligibilityPage = pathname.endsWith('/welcome/eligibility');
  const isPropertyAndProgramPage = pathname.endsWith(
    '/welcome/property-and-program',
  );
  const { data: affordaPortalDetails } = useAffordaPortalDetailsQuery();
  const { isLoading, mutateAsync: updateAffordaPortalDetails } =
    useUpdateAffordaPortalDetailsMutation();
  const handleContinue = async () => {
    const welcomeCompletedAt = new Date().toISOString();
    if (!affordaPortalDetails?.welcomeCompletedAt) {
      await updateAffordaPortalDetails({ welcomeCompletedAt });
    }
  };

  return {
    to: {
      [`${baseURL}/welcome`]: `${baseURL}/welcome/property-and-program`,
      [`${baseURL}/welcome/property-and-program`]: `${baseURL}/welcome/eligibility`,
      [`${baseURL}/welcome/eligibility`]: null,
      [`${baseURL}/welcome/results-eligible`]: `${baseURL}/dashboard`,
      [`${baseURL}/welcome/results-ineligible`]: `${baseURL}/dashboard`,
    }[pathname],
    type: isEligibilityPage ? 'submit' : 'button',
    disabled: isEligibilityPage ? !ReactHookFormProps?.isValid : false,
    isSubmitting: isLoading,
    onClick: isEligibilityPage
      ? onSubmit
      : isPropertyAndProgramPage
        ? handleContinue
        : undefined,
  };
};

const useEligibilityForm = ({ history, organizationId, propertyId }) => {
  const [propParamError, setPropParamError] = useState('');

  const { updateForm } = useFormStateManager(SECTION_NAMES.WELCOME_AND_QUALIFY);

  const prequalifyOptions = useAsyncPrequalifyOptions(setPropParamError);
  const ReactHookFormProps = useReactHookFormProps({
    defaultValues: {
      totalAnnualHouseholdIncome: '',
      numberOfPeopleInUnit: '',
    },
    mode: 'onChange',
  });
  const { control, handleSubmit } = ReactHookFormProps;

  const onSubmit = handleSubmit(
    ({ numberOfPeopleInUnit, totalAnnualHouseholdIncome }) => {
      const baseUrl = `/${organizationId}/${propertyId}`;
      const eligible = `${baseUrl}/welcome/results-eligible`;
      const ineligible = `${baseUrl}/welcome/results-ineligible`;

      updateForm(
        { numberOfPeopleInUnit, totalAnnualHouseholdIncome },
        { control, fields: FIELDS },
      );

      if (propParamError || !prequalifyOptions) return history.push(ineligible);

      const options = prequalifyOptions[numberOfPeopleInUnit - 1] || null;

      if (options?.value && totalAnnualHouseholdIncome <= options.value) {
        history.push(eligible);
      } else {
        history.push(ineligible);
      }
    },
  );

  return {
    eligibilityFields: FIELDS,
    onSubmit,
    ReactHookFormProps,
  };
};

const useWelcomePage = ({ baseURL }) => {
  const history = useHistory();

  const {
    userProfile: { firstName },
    isLoading,
  } = useContext(UserContext);
  const {
    marketingInfo: { name, propertyLogoLink },
    organizationId,
    propertyId,
  } = useContext(AppContext);

  const { eligibilityFields, onSubmit, ReactHookFormProps } =
    useEligibilityForm({
      history,
      organizationId,
      propertyId,
    });

  const continueButtonProps = useContinueButtonProps({
    baseURL,
    history,
    onSubmit,
    ReactHookFormProps,
  });

  const isFirstPage = history.location.pathname === `${baseURL}/welcome`;

  return {
    eligibilityFields,
    isFirstPage,
    firstName,
    isLoading,
    name,
    propertyLogoLink,
    organizationId,
    propertyId,
    continueButtonProps,
    onSubmit,
    ReactHookFormProps,
  };
};

export default useWelcomePage;
